import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  inject,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  TemplateRef,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { IdbaMatTableCellComponent } from '../idba-mat-table-cell/idba-mat-table-cell';
import { IdbaMatTableFooterComponent } from '../idba-mat-table-footer/idba-mat-table-footer';
import {
  Coluna,
  IdbaTableAction,
  IdbaTableBulkAction,
  IdbaTableSelectionConfig,
} from '../../models/mat-table-model';

@Component({
  selector: 'idba-responsive-mat-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    DragDropModule,
    IdbaMatTableCellComponent,
    IdbaMatTableFooterComponent,
  ],
  templateUrl: './responsive-mat-table.component.html',
  styleUrls: ['./responsive-mat-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ResponsiveMatTableComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() columns: Coluna[] = [];
  @Input() dataSource: MatTableDataSource<any> | any[] = new MatTableDataSource<any>();
  @Input() paginator?: MatPaginator;
  @Input() sort?: MatSort;
  @Input() rowActions: IdbaTableAction[] = [];
  @Input() bulkActions: IdbaTableBulkAction[] = [];
  @Input() selection: IdbaTableSelectionConfig = { enabled: false };
  @Input() actionsLabel = 'Ações';
  @Input() showColumnSelector = true;
  @Input() showRefreshButton = false;
  @Input() observeContainerResize = true;
  @Input() columnPreferenceKey?: string;
  /** Conteúdo adicional por linha, preservando tabelas/edições internas em migrações responsivas. */
  @Input() detailTemplate?: TemplateRef<any>;
  @Output() selectedRowsChange = new EventEmitter<any[]>();
  @Output() refreshRequested = new EventEmitter<void>();
  @Output() expandedChange = new EventEmitter<{ row: any; expanded: boolean }>();

  @ViewChild('tableWrapper', { static: true }) tableWrapper: ElementRef<HTMLElement>;
  @ViewChild(MatSort) internalSort?: MatSort;
  @ViewChild(MatMenuTrigger) columnMenuTrigger?: MatMenuTrigger;

  displayedColumns: string[] = [];
  hiddenColumns: Coluna[] = [];
  expandedRows = new Set<any>();
  selectionModel = new SelectionModel<any>(true, []);
  columnSearch = '';
  pendingVisibleColumns = new Set<string>();
  pendingColumnOrder: string[] = [];
  private defaultVisibleColumnDefs: string[] = [];
  private defaultColumnOrder: string[] = [];
  private resizeObserver?: ResizeObserver;
  private paginatorSubscription?: Subscription;
  private boundPaginator?: MatPaginator;
  private savedPageSize?: number;
  private lastObservedWidth?: number;
  private router = inject(Router);

  ngAfterViewInit(): void {
    this.bindDataSource();
    this.applyDefaultSort();
    if (this.observeContainerResize) {
      this.lastObservedWidth = this.tableWrapper.nativeElement.clientWidth;
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = Math.round(entries[0]?.contentRect.width ?? 0);
        if (!width || width === this.lastObservedWidth) return;
        this.lastObservedWidth = width;
        this.calculateColumns();
      });
      this.resizeObserver.observe(this.tableWrapper.nativeElement);
      this.calculateColumns();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource'] || changes['paginator'] || changes['sort']) {
      this.bindDataSource();
    }
    if (changes['columns'] || changes['selection'] || changes['rowActions'] || changes['dataSource']) {
      if (changes['columns']) this.initializeColumnPreference();
      this.selectionModel = new SelectionModel<any>(
        this.selection.multiple !== false,
        this.selectionModel.selected,
      );
      this.calculateColumns();
    }
    this.applyDefaultSort();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.paginatorSubscription?.unsubscribe();
  }

  get tableColumns(): string[] {
    const result: string[] = [];
    if (this.selection.enabled) result.push('__selection');
    if (this.hasExpandableRows) result.push('__expand');
    result.push(...this.displayedColumns);
    if (this.rowActions.length) result.push('__actions');
    return result;
  }

  get detailRowColumns(): string[] {
    return ['__detail'];
  }

  get hasExpandableRows(): boolean {
    return this.hiddenColumns.length > 0 || !!this.detailTemplate;
  }

  columnFlex(column: Coluna): string {
    return `1 1 ${this.columnWidth(column)}px`;
  }

  get hasFooter(): boolean {
    return (this.columns ?? []).some(
      (column) => column.display && (column.calculateFooter || column.footer !== undefined),
    );
  }

  get actionColumnFlex(): string {
    return `0 0 ${this.actionColumnWidth()}px`;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.calculateColumns();
  }

  get selectedRows(): any[] {
    return this.selectionModel.selected;
  }

  get configurableColumns(): Coluna[] {
    return (this.columns || []).filter((column) => !column.detailOnly);
  }

  get filteredConfigurableColumns(): Coluna[] {
    const positions = new Map(
      this.pendingColumnOrder.map((columnDef, index) => [columnDef, index]),
    );
    const orderedColumns = [...this.configurableColumns].sort((a, b) => {
      const positionA = positions.get(a.columnDef);
      const positionB = positions.get(b.columnDef);
      if (positionA === undefined || positionB === undefined) return 0;
      return positionA - positionB;
    });
    const search = this.normalizeText(this.columnSearch.trim());
    if (!search) return orderedColumns;
    return orderedColumns.filter((column) => this.normalizeText(column.header).includes(search));
  }

  get visibleColumnCount(): number {
    return this.configurableColumns.filter((column) => column.display).length;
  }

  prepareColumnSelection(): void {
    this.columnSearch = '';
    this.pendingVisibleColumns = new Set(
      this.configurableColumns
        .filter((column) => column.display || column.neverHide)
        .map((column) => column.columnDef),
    );
    this.pendingColumnOrder = this.configurableColumns.map((column) => column.columnDef);
  }

  isColumnPending(column: Coluna): boolean {
    return column.neverHide || this.pendingVisibleColumns.has(column.columnDef);
  }

  togglePendingColumn(column: Coluna, checked: boolean): void {
    if (column.neverHide) return;
    const selection = new Set(this.pendingVisibleColumns);
    checked ? selection.add(column.columnDef) : selection.delete(column.columnDef);
    this.pendingVisibleColumns = selection;
  }

  resetPendingColumns(): void {
    this.pendingVisibleColumns = new Set(this.defaultVisibleColumnDefs);
    this.pendingColumnOrder = [...this.defaultColumnOrder];
    this.configurableColumns
      .filter((column) => column.neverHide)
      .forEach((column) => this.pendingVisibleColumns.add(column.columnDef));
  }

  dropColumn(event: CdkDragDrop<Coluna[]>): void {
    if (this.columnSearch || event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.pendingColumnOrder, event.previousIndex, event.currentIndex);
  }

  applyColumnSelection(): void {
    this.applyColumnOrder(this.pendingColumnOrder);
    (this.columns || []).forEach((column) => {
      column.display =
        column.detailOnly || column.neverHide || this.pendingVisibleColumns.has(column.columnDef);
    });
    this.saveColumnPreference();
    this.calculateColumns();
    this.columnMenuTrigger?.closeMenu();
  }

  isSelected(row: any): boolean {
    return this.selectionModel.isSelected(row);
  }

  canSelect(row: any): boolean {
    return (
      this.selection.enabled &&
      (!this.selection.selectPredicate || this.selection.selectPredicate(row))
    );
  }

  toggleRow(row: any): void {
    if (!this.canSelect(row)) return;
    if (!this.selection.multiple && !this.selectionModel.isSelected(row))
      this.selectionModel.clear();
    this.selectionModel.toggle(row);
    this.selectedRowsChange.emit(this.selectedRows);
  }

  toggleAll(): void {
    const selectable = this.dataRows.filter((row) => this.canSelect(row));
    if (this.isAllSelected()) selectable.forEach((row) => this.selectionModel.deselect(row));
    else selectable.forEach((row) => this.selectionModel.select(row));
    this.selectedRowsChange.emit(this.selectedRows);
  }

  isAllSelected(): boolean {
    const selectable = this.dataRows.filter((row) => this.canSelect(row));
    return selectable.length > 0 && selectable.every((row) => this.selectionModel.isSelected(row));
  }

  isPartiallySelected(): boolean {
    const selectable = this.dataRows.filter((row) => this.canSelect(row));
    const count = selectable.filter((row) => this.selectionModel.isSelected(row)).length;
    return count > 0 && count < selectable.length;
  }

  toggleExpanded(row: any): void {
    this.expandedRows.has(row) ? this.expandedRows.delete(row) : this.expandedRows.add(row);
    this.expandedChange.emit({ row, expanded: this.expandedRows.has(row) });
  }

  isExpanded(row: any): boolean {
    return this.expandedRows.has(row);
  }

  visibleAction(action: IdbaTableAction, row: any): boolean {
    return !action.visible || action.visible(row);
  }

  invokeAction(action: IdbaTableAction, row: any): void {
    if ((!action.disabled || !action.disabled(row)) && action.execute) action.execute(row);
  }

  actionQueryParams(action: IdbaTableAction, row: any): any {
    const queryParams = action.queryParams ? action.queryParams(row) : {};
    if (!action.routerLink) return queryParams;

    return {
      ...queryParams,
      returnUrl: this.router.url,
    };
  }

  bulkActionEnabled(action: IdbaTableBulkAction): boolean {
    return this.selectedRows.length > 0 && (!action.enabled || action.enabled(this.selectedRows));
  }

  executeBulkAction(action: IdbaTableBulkAction): void {
    if (!this.bulkActionEnabled(action)) return;
    const rows = [...this.selectedRows];
    action.execute(rows);
    this.selectionModel.clear();
    this.selectedRowsChange.emit([]);
  }

  private initializeColumnPreference(): void {
    this.defaultVisibleColumnDefs = this.configurableColumns
      .filter((column) => column.display)
      .map((column) => column.columnDef);
    this.defaultColumnOrder = this.configurableColumns.map((column) => column.columnDef);
    if (!this.columnPreferenceKey) return;
    this.loadPageSizePreference();
    try {
      const savedPreference = JSON.parse(
        globalThis.localStorage?.getItem(this.columnPreferenceKey) || 'null',
      );
      const savedColumnDefs = Array.isArray(savedPreference)
        ? savedPreference
        : savedPreference?.visible;
      const savedColumnOrder = Array.isArray(savedPreference?.order) ? savedPreference.order : [];
      if (!Array.isArray(savedColumnDefs)) return;
      const savedColumns = new Set(
        savedColumnDefs.filter((columnDef) => typeof columnDef === 'string'),
      );
      this.applyColumnOrder(savedColumnOrder);
      (this.columns || []).forEach((column) => {
        column.display =
          column.detailOnly || column.neverHide || savedColumns.has(column.columnDef);
      });
    } catch {
      try {
        globalThis.localStorage?.removeItem(this.columnPreferenceKey);
      } catch {}
    }
  }

  private saveColumnPreference(): void {
    if (!this.columnPreferenceKey) return;
    try {
      globalThis.localStorage?.setItem(
        this.columnPreferenceKey,
        JSON.stringify({
          visible: this.configurableColumns
            .filter((column) => column.display)
            .map((column) => column.columnDef),
          order: this.configurableColumns.map((column) => column.columnDef),
        }),
      );
    } catch {}
  }

  private applyColumnOrder(columnOrder: string[]): void {
    if (!columnOrder.length || !this.columns?.length) return;
    const positions = new Map(columnOrder.map((columnDef, index) => [columnDef, index]));
    const configurable = this.configurableColumns
      .map((column, index) => ({ column, index }))
      .sort((a, b) => {
        const positionA = positions.get(a.column.columnDef);
        const positionB = positions.get(b.column.columnDef);
        if (positionA === undefined && positionB === undefined) return a.index - b.index;
        if (positionA === undefined) return 1;
        if (positionB === undefined) return -1;
        return positionA - positionB;
      })
      .map((item) => item.column);
    const detailOnly = this.columns.filter((column) => column.detailOnly);
    this.columns.splice(0, this.columns.length, ...configurable, ...detailOnly);
  }

  private normalizeText(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('pt-BR');
  }

  private bindDataSource(): void {
    if (!(this.dataSource instanceof MatTableDataSource)) return;
    if (this.sort || this.internalSort) this.dataSource.sort = this.sort || this.internalSort!;
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
      this.bindPaginatorPreference();
    }
  }

  private applyDefaultSort(): void {
    if (!this.internalSort) return;

    const defaultSort = this.columns?.find((column) => column?.sort);
    if (!defaultSort) return;

    this.internalSort.active = defaultSort.columnDef;
    this.internalSort.direction = defaultSort.sortDirection === 'asc' ? 'asc' : 'desc';
    this.internalSort.sortChange.emit({
      active: this.internalSort.active,
      direction: this.internalSort.direction,
    });
  }

  private get dataRows(): any[] {
    return Array.isArray(this.dataSource) ? this.dataSource : this.dataSource?.data || [];
  }

  private bindPaginatorPreference(): void {
    if (!this.paginator || this.boundPaginator === this.paginator) return;
    this.paginatorSubscription?.unsubscribe();
    this.boundPaginator = this.paginator;
    this.restorePageSize();
    this.paginatorSubscription = this.paginator.page.subscribe(({ pageSize }) => {
      if (pageSize === this.savedPageSize) return;
      this.savedPageSize = pageSize;
      this.savePageSizePreference();
    });
  }

  private loadPageSizePreference(): void {
    if (!this.columnPreferenceKey) return;
    try {
      const savedPageSize = Number(
        globalThis.sessionStorage?.getItem(`${this.columnPreferenceKey}.page-size`),
      );
      this.savedPageSize =
        Number.isInteger(savedPageSize) && savedPageSize > 0 ? savedPageSize : undefined;
      this.restorePageSize();
    } catch {}
  }

  private savePageSizePreference(): void {
    if (!this.columnPreferenceKey || !this.savedPageSize) return;
    try {
      globalThis.sessionStorage?.setItem(
        `${this.columnPreferenceKey}.page-size`,
        String(this.savedPageSize),
      );
    } catch {}
  }

  private restorePageSize(): void {
    if (!this.paginator || !this.savedPageSize) return;
    this.paginator.pageSize = this.savedPageSize;
  }

  private isCompactViewport(): boolean {
    return (typeof window !== 'undefined' ? window.innerWidth : 0) <= 959;
  }

  private actionColumnWidth(): number {
    if (this.isCompactViewport()) {
      // In compact mode each action button has 40px plus 1px margins on both
      // sides, and the action cell has 16px of horizontal padding.
      const actionButtonWidth = 42;
      const actionGap = 4;
      const actionCellPadding = 16;
      return Math.min(180, Math.max(
        56,
        this.rowActions.length * actionButtonWidth +
          Math.max(this.rowActions.length - 1, 0) * actionGap +
          actionCellPadding,
      ));
    }

    const width =
      this.rowActions.reduce(
        (total, action) => total + 42 + (action.label?.length || 0) * 7 + 16,
        0,
      ) + 24;
    // Ações são renderizadas lado a lado quando há espaço e quebram linha
    // dentro da célula quando necessário. Reservar a soma integral de todos
    // os rótulos pode consumir toda a largura da tabela e colapsar as demais
    // colunas para poucos pixels.
    return Math.min(360, Math.max(width, 120));
  }

  private columnWidth(column: Coluna): number {
    if (column.minWidth) {
      return column.minWidth;
    }

    const contentLength = Math.max(column.header?.length || 0, column.maxChars || 0);
    return Math.max(90, Math.min(280, contentLength ? contentLength * 8 + 32 : 120));
  }

  private calculateColumns(): void {
    if (!this.tableWrapper || !this.columns?.length) return;
    const availableWidth = this.tableWrapper.nativeElement.clientWidth || window.innerWidth;
    const configured = this.columns.filter((column) => column.display && !column.detailOnly);
    const mandatory = configured.filter((column) => column.neverHide);
    const flexible = configured.filter(
      (column) => !column.neverHide && column.responsive !== false,
    );
    const fixed = configured.filter((column) => !column.neverHide && column.responsive === false);
    const widthFor = (column: Coluna) => this.columnWidth(column);
    const actionWidth = this.rowActions.length ? this.actionColumnWidth() : 0;
    const selectionWidth = this.selection.enabled ? 52 : 0;
    const calculateVisible = (expandWidth: number): Coluna[] => {
      let used =
        actionWidth +
        selectionWidth +
        expandWidth +
        mandatory.reduce((sum, column) => sum + widthFor(column), 0);
      const visible = [...mandatory];
      for (const column of fixed) {
        if (used + widthFor(column) <= availableWidth || visible.length === 0) {
          visible.push(column);
          used += widthFor(column);
        }
      }
      for (const column of flexible) {
        if (used + widthFor(column) <= availableWidth) {
          visible.push(column);
          used += widthFor(column);
        }
      }
      return visible;
    };

    // The expand column is only rendered when at least one configured column
    // is hidden. Recalculate with its width so the selected columns and their
    // flex layout use the same budget as the rendered table.
    let visible = calculateVisible(0);
    if (visible.length < configured.length) visible = calculateVisible(48);

    this.displayedColumns = configured
      .filter((column) => visible.includes(column))
      .map((column) => column.columnDef);
    this.hiddenColumns = [
      ...configured.filter((column) => !visible.includes(column)),
      ...this.columns.filter((column) => column.display && column.detailOnly),
    ];
  }

  private calculateActionColumnWidth(): number {
    if (!this.rowActions.length) return 0;
    const compact = (this.tableWrapper?.nativeElement.clientWidth || window.innerWidth) <= 959;
    const actionGroups = this.dataRows.length
      ? this.dataRows.map((row) => this.rowActions.filter((action) => this.visibleAction(action, row)))
      : [this.rowActions];
    const widths = actionGroups.map((actions) => {
      if (compact) return actions.length * 44 + 12;
      return actions.reduce(
        (total, action) => total + 58 + (action.label?.length || 0) * 7,
        24,
      );
    });
    return Math.max(compact ? 56 : 120, ...widths);
  }
}
